import { Component, signal, inject, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ApiService } from './api.service';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [FormsModule, CommonModule],
  template: `
    <div class="min-h-screen flex font-sans" style="background:#f1f9fe;">
      <!-- Left Banner -->
      <div class="hidden lg:flex lg:w-1/2 relative overflow-hidden flex-col items-center justify-center p-12" style="background:linear-gradient(145deg, #0C3A5A 0%, #1A6FA6 50%, #2B9AD2 100%);">
        <div class="absolute -top-20 -left-20 w-72 h-72 rounded-full opacity-10" style="background:#5EC9F0;"></div>
        <div class="absolute -bottom-32 -right-16 w-96 h-96 rounded-full opacity-10" style="background:#F6AF26;"></div>
        <div class="relative z-10 text-center">
          <h2 class="text-white text-3xl font-bold mb-4">Set New Password</h2>
          <p class="text-white/70 text-base max-w-sm mx-auto">Create a strong password to secure your admin account. Avoid using previous passwords.</p>
        </div>
      </div>

      <!-- Right Form -->
      <div class="flex-1 flex items-center justify-center p-8">
        <div class="w-full max-w-md">
          <div class="bg-white rounded-3xl p-8 shadow-sm" style="border:1px solid #EEF2F7;">
            <h1 class="text-2xl font-bold text-slate-900 mb-1">New Password</h1>
            <p class="text-slate-500 text-sm mb-6">Create your new secure password.</p>

            @if (errorMsg()) {
              <div class="p-3.5 mb-5 rounded-2xl flex items-center gap-3 text-sm font-medium" style="background:#FEF2F2; color:#EF4444; border:1px solid #FECACA;">
                <i class="ti ti-alert-circle text-[18px] shrink-0"></i>{{ errorMsg() }}
              </div>
            }

            @if (successMsg()) {
              <div class="p-3.5 mb-5 rounded-2xl flex items-center gap-3 text-sm font-medium" style="background:#ECFDF5; color:#059669; border:1px solid #A7F3D0;">
                <i class="ti ti-circle-check text-[18px] shrink-0"></i>{{ successMsg() }}
              </div>
            }

            <form (submit)="onSubmit()" class="space-y-5">
              <div>
                <label class="block text-sm font-semibold text-slate-700 mb-1.5">New Password</label>
                <input type="password" [(ngModel)]="password" name="password" placeholder="••••••••" class="w-full h-12 px-4 rounded-2xl text-sm border outline-none" style="border-color:#E2E8F0; background:#FAFBFC;">
              </div>
              <div>
                <label class="block text-sm font-semibold text-slate-700 mb-1.5">Confirm Password</label>
                <input type="password" [(ngModel)]="confirmPassword" name="confirmPassword" placeholder="••••••••" class="w-full h-12 px-4 rounded-2xl text-sm border outline-none" style="border-color:#E2E8F0; background:#FAFBFC;">
              </div>
              <button type="submit" [disabled]="loading()" class="w-full h-12 rounded-2xl font-bold text-white shadow-sm hover:opacity-90 transition-all flex items-center justify-center" style="background:linear-gradient(135deg, #2B9AD2, #1A6FA6);">
                {{ loading() ? 'Updating...' : 'Update Password' }}
              </button>
            </form>

            <div class="mt-6 text-center">
              <a href="/login" class="text-sm font-bold text-[#2B9AD2] hover:underline flex items-center justify-center gap-1"><i class="ti ti-arrow-left"></i> Back to Login</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  `
})
export class ResetPasswordComponent implements OnInit {
  token = '';
  password = '';
  confirmPassword = '';
  loading = signal(false);
  errorMsg = signal('');
  successMsg = signal('');

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private api = inject(ApiService);

  ngOnInit() {
    this.token = this.route.snapshot.queryParamMap.get('token') || '';
    if (!this.token) {
      this.errorMsg.set('Invalid link. Token is missing.');
    }
  }

  onSubmit() {
    if (!this.password) {
      this.errorMsg.set('Password is required.');
      return;
    }
    if (this.password !== this.confirmPassword) {
      this.errorMsg.set('Passwords do not match.');
      return;
    }

    this.loading.set(true);
    this.errorMsg.set('');

    const payload = {
      token: this.token,
      password: this.password
    };

    this.api.submitResetPassword(payload).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.successMsg.set('Password updated successfully! Redirecting to login...');
          setTimeout(() => this.router.navigate(['/login']), 3000);
        } else {
          this.errorMsg.set(res.message || 'Failed to update password.');
        }
        this.loading.set(false);
      },
      error: (err: any) => {
        this.errorMsg.set('An error occurred. Please try again.');
        this.loading.set(false);
      }
    });
  }
}
